import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import { OrderCreationLayout } from '@/components/order/OrderCreationLayout'
import { Calendar, Users, FileText, ArrowRight, AlertCircle, Clock } from 'lucide-react'
import {
  getCategoriesWithTemplates,
  getOrderTemplate,
} from '@/services/orderApi'
import { useOrderCreationStore } from '@/store/orderCreationStore'
import { useLocalization } from '@/contexts/LocalizationContext'
import { getOrderTemplateName, getOrderTemplateDescription } from '@/utils/orderLocalization'
import type { ServiceCategory, OrderTemplate } from '@/services/orderApi'

export function TemplateSelectionPage() {
  const navigate = useNavigate()
  const { locale, t } = useLocalization()
  const {
    selectedCategory,
    clientProfileId,
    businessClientProfileId,
    businessAccountId,
    setSelectedTemplate,
    setCurrentStep,
    getCreateOrderEntryPath,
  } = useOrderCreationStore()
  const orderSubjectProfileId = clientProfileId || businessClientProfileId
  const [categories, setCategories] = useState<ServiceCategory[]>([])
  const [templates, setTemplates] = useState<OrderTemplate[]>([])
  const [loading, setLoading] = useState(true)
  const [loadingTemplate, setLoadingTemplate] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!orderSubjectProfileId) {
      navigate(getCreateOrderEntryPath(), { replace: true })
      return
    }
    if (!selectedCategory) {
      navigate('/create-order/category', { replace: true })
      return
    }
    loadTemplates()
  }, [selectedCategory, orderSubjectProfileId, businessAccountId, navigate, getCreateOrderEntryPath])

  const loadTemplates = async () => {
    if (!selectedCategory) return
    try {
      setLoading(true)
      setError(null)
      const data = await getCategoriesWithTemplates(
        businessClientProfileId ? businessAccountId : null
      )
      setCategories(data)

      const category = data.find(
        (c) => c.id === selectedCategory.id || c.name === selectedCategory.nameEn
      )
      if (category) {
        const validTemplates = (category.orderTemplates || []).filter(
          (t): t is OrderTemplate => t != null && t.id != null
        )
        setTemplates(validTemplates)
        
        if (category.templatesCount > 0 && validTemplates.length === 0) {
          console.warn(
            `Category has ${category.templatesCount} templates but none were returned.`
          )
        }
      } else {
        setError(t('orderCreation.categoryNotFound'))
      }
    } catch (err: any) {
      setError(err.message || t('orderCreation.failedToLoadTemplates'))
      console.error('Error loading templates:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleSelectTemplate = async (template: OrderTemplate) => {
    try {
      setLoadingTemplate(template.id)
      const fullTemplate = await getOrderTemplate(template.id)
      setSelectedTemplate(fullTemplate)
      setCurrentStep(4)
      navigate('/create-order/appointments')
    } catch (err: any) {
      setError(err.message || t('orderCreation.failedToLoadTemplateDetails'))
      console.error('Error loading template details:', err)
    } finally {
      setLoadingTemplate(null)
    }
  }

  const getCategoryName = () => {
    if (!selectedCategory) return ''
    return locale === 'ar' ? (selectedCategory.nameAr || selectedCategory.nameEn) : (selectedCategory.nameEn || selectedCategory.nameAr)
  }

  const renderContent = () => {
    if (loading) {
      return (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-6 w-3/4" />
                <Skeleton className="h-4 w-full mt-2" />
              </CardHeader>
              <CardContent>
                <div className="flex gap-4 mb-4">
                  <Skeleton className="h-5 w-24" />
                  <Skeleton className="h-5 w-24" />
                </div>
                <Skeleton className="h-10 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      )
    }

    if (error) {
      return (
        <Card className="border-destructive">
          <CardHeader>
            <div className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              <CardTitle>
                {t('orderCreation.errorLoadingTemplates')}
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground mb-4">{error}</p>
            <Button onClick={loadTemplates}>
              {t('common.retry')}
            </Button>
          </CardContent>
        </Card>
      )
    }

    if (templates.length === 0) {
      return (
        <Card>
          <CardContent className="py-12 text-center">
            <FileText className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground mb-2">
              {t('orderCreation.noTemplatesAvailable')}
            </p>
          </CardContent>
        </Card>
      )
    }

    return (
      <div className="space-y-4">
        {templates.map((template) => (
          <Card
            key={template.id}
            className={`cursor-pointer hover:shadow-lg hover:border-primary/50 transition-all group ${
              loadingTemplate === template.id ? 'opacity-70 pointer-events-none' : ''
            }`}
            onClick={() => handleSelectTemplate(template)}
          >
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <CardTitle className="text-lg">
                      {getOrderTemplateName(template, locale)}
                    </CardTitle>
                    <Badge variant="secondary" className="text-xs">
                      v{template.version}
                    </Badge>
                  </div>
                  {(getOrderTemplateDescription(template, locale) || template.description) && (
                    <CardDescription className="text-sm text-muted-foreground whitespace-pre-wrap break-words">
                      {getOrderTemplateDescription(template, locale) || template.description}
                    </CardDescription>
                  )}
                </div>
                <ArrowRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0 mt-1" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap items-center gap-4 mb-4 text-sm text-muted-foreground">
                <div className="flex items-center gap-1.5">
                  <Calendar className="h-4 w-4" />
                  <span>
                    {template.appointmentSpecsCount}{' '}
                    {t('orderCreation.appointments')}
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Users className="h-4 w-4" />
                  <span>
                    {template.ordersCount}{' '}
                    {t('orderCreation.orders')}
                  </span>
                </div>
                {template.appointmentSpecs && template.appointmentSpecs.length > 0 && (
                  <div className="flex items-center gap-1.5">
                    <Clock className="h-4 w-4" />
                    <span>
                      {template.appointmentSpecs.reduce((acc, spec) => acc + spec.durationMinutes, 0)}{' '}
                      {t('orderCreation.minutesTotal')}
                    </span>
                  </div>
                )}
              </div>
              <Button 
                className="w-full" 
                variant="outline"
                disabled={loadingTemplate === template.id}
              >
                {loadingTemplate === template.id
                  ? t('common.loading')
                  : t('orderCreation.selectTemplate')}
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>
    )
  }

  if (!orderSubjectProfileId || !selectedCategory) {
    return null
  }

  return (
    <OrderCreationLayout
      titleKey="orderCreation.selectServiceTemplate"
      description={t('orderCreation.selectServiceTemplateDescription', { category: getCategoryName() })}
      step={3}
      backPath="/create-order/category"
    >
      <div className="max-w-3xl mx-auto">
        {/* Category Info */}
        {selectedCategory && (
          <Card className="mb-6 bg-muted/30">
            <CardContent className="py-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <FileText className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">
                    {t('orderCreation.selectedCategory')}
                  </p>
                  <p className="font-semibold">{getCategoryName()}</p>
                </div>
                <Badge variant="secondary" className="ms-auto">
                  {t('orderCreation.stepOf', { step: 3, total: 7 })}
                </Badge>
              </div>
            </CardContent>
          </Card>
        )}

        {renderContent()}
      </div>
    </OrderCreationLayout>
  )
}
